import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { ShieldOutlined, VerifiedUserOutlined, LockOutlined } from '@mui/icons-material';
import { authService } from '../../services/api';
import { fetchCurrentUser } from '../../redux/slices/authSlice';
import RecoveryCodes from './RecoveryCodes';

/**
 * In-app security settings: enable/disable two-factor authentication.
 *
 * - Optional users can enable (opt-in) or disable MFA here.
 * - Mandatory users (admins / configured roles) see an always-on state and
 *   cannot disable (the backend also enforces this).
 */
const SecuritySettingsPage = () => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null); // {requirement, enabled, opt_in}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Enable flow
  const [provisioning, setProvisioning] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable flow
  const [disabling, setDisabling] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authService.mfaStatus();
      setStatus(res.data);
    } catch {
      setError('Could not load your security settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const startEnable = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await authService.mfaSetupBegin();
      setProvisioning(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start MFA setup.');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    const otp = code.trim();
    if (!otp) return;
    setError(null);
    setBusy(true);
    try {
      const res = await authService.mfaSetupConfirm(otp);
      setRecoveryCodes(res.data.recovery_codes || []);
      setProvisioning(null);
      setCode('');
      await loadStatus();
      dispatch(fetchCurrentUser());
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async (e) => {
    e.preventDefault();
    const otp = disableCode.trim();
    if (!otp) return;
    setError(null);
    setBusy(true);
    try {
      await authService.mfaDisable(otp);
      setDisabling(false);
      setDisableCode('');
      setRecoveryCodes(null);
      await loadStatus();
      dispatch(fetchCurrentUser());
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not disable MFA.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isMandatory = status?.requirement === 'mandatory';
  const isEnabled = status?.enabled;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <ShieldOutlined sx={{ color: '#1565C0' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Security</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Two-Factor Authentication</Typography>
          {isEnabled ? (
            <Chip icon={<VerifiedUserOutlined />} label="Enabled" color="success" size="small" />
          ) : (
            <Chip label="Disabled" size="small" />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isMandatory
            ? 'Two-factor authentication is required for your account by your organization.'
            : 'Add an extra layer of security by requiring a code from your authenticator app at sign-in.'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Recovery codes shown right after enabling */}
        {recoveryCodes && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Save these recovery codes now — they won't be shown again.
            </Alert>
            <RecoveryCodes codes={recoveryCodes} />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Already enabled */}
        {isEnabled && !recoveryCodes && (
          isMandatory ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <LockOutlined fontSize="small" />
              <Typography variant="body2">This setting is locked by your organization and cannot be disabled.</Typography>
            </Box>
          ) : disabling ? (
            <form onSubmit={confirmDisable}>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Enter a current code from your authenticator (or a recovery code) to disable.
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Verification code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                disabled={busy}
                autoFocus
                inputProps={{ inputMode: 'numeric' }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button type="submit" variant="contained" color="error" disabled={busy || !disableCode.trim()}>
                  {busy ? <CircularProgress size={20} /> : 'Disable MFA'}
                </Button>
                <Button onClick={() => { setDisabling(false); setDisableCode(''); }} disabled={busy}>Cancel</Button>
              </Box>
            </form>
          ) : (
            <Button variant="outlined" color="error" onClick={() => setDisabling(true)}>
              Disable two-factor authentication
            </Button>
          )
        )}

        {/* Not enabled — enable flow */}
        {!isEnabled && (
          provisioning ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Scan this QR code with your authenticator app, then enter the 6-digit code.
              </Typography>
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Box
                  component="img"
                  alt="Authenticator QR code"
                  src={`data:image/png;base64,${provisioning.qr_png_base64}`}
                  sx={{ width: 180, height: 180, border: '1px solid #e9ecef', borderRadius: 2, p: 1 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">Manual key:</Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all', mb: 2 }}>
                {provisioning.secret}
              </Typography>
              <form onSubmit={confirmEnable}>
                <TextField
                  fullWidth
                  size="small"
                  label="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={busy}
                  autoFocus
                  inputProps={{ inputMode: 'numeric' }}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" disabled={busy || !code.trim()}>
                    {busy ? <CircularProgress size={20} /> : 'Confirm & Enable'}
                  </Button>
                  <Button onClick={() => { setProvisioning(null); setCode(''); }} disabled={busy}>Cancel</Button>
                </Box>
              </form>
            </Box>
          ) : (
            <Button variant="contained" onClick={startEnable} disabled={busy}
              sx={{ bgcolor: '#2C3E50', '&:hover': { bgcolor: '#1A242F' } }}>
              {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Enable two-factor authentication'}
            </Button>
          )
        )}
      </Paper>
    </Container>
  );
};

export default SecuritySettingsPage;
